import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { IonNav } from '@ionic/angular';
import { MedicalInquiryService, MI_TAB } from '../../services/medical-inquiry/medical-inquiry.service';
import { TranslateService } from '@ngx-translate/core';
import { IndTabsDataModel } from '@omni/models/ind-tabs-data-model';
import { ChildNavNames, NavigationService, PageName } from '@omni/services/navigation/navigation.service';
import { DeviceService } from '@omni/services/device/device.service';
import { AuthenticationService } from '@omni/services/authentication.service';
import { FeatureActionsMap } from '@omni/classes/authentication/user.class';
import { combineLatest, Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'page-medical-inquiry-home[base-page]',
  templateUrl: './medical-inquiry-home.html',
  styleUrls: ['./medical-inquiry-home.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MedicalInquiryHomePage implements OnInit, OnDestroy {
  tabsData: IndTabsDataModel[] = [];
  selectedTab: string = MI_TAB.MY;
  @ViewChild('medicalinquiryrightpane', { static: true }) navCtrl: IonNav;
  private destroy$ = new Subject<void>();
  /** True while the right pane hosts a pushed page rather than this tool's own views. */
  isHostingPushedPage = false;

  constructor(
    public medicalInquiryService: MedicalInquiryService,
    private readonly translate: TranslateService,
    private readonly navService: NavigationService,
    private readonly device: DeviceService,
    private readonly authService: AuthenticationService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.medicalInquiryService.setShowNew(false);
    this.medicalInquiryService.setSelectedMI(null);
    this.setTabsData();

    this.navService.initRightPaneChildNav(
      this.navCtrl,
      ChildNavNames.MedicalInquiryNavigation,
      PageName.MedicalInquiryHomePage,
      false,
      { deviceString: this.device.deviceFlags?.ios ? 'ios' : 'other' }
    );

    // A pushed page (a quick-created meeting) sets neither selectedMI nor showNew, so the
    // right pane would stay collapsed. OnPush needs the emission to repaint.
    this.navService.childNavRightPaneView$.pipe(takeUntil(this.destroy$)).subscribe(isHosting => {
      this.isHostingPushedPage = isHosting;
      this.cdr.detectChanges();
    });

    this.device.isOfflineObservable.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.setTabsData();
      this.cdr.markForCheck();
    });

    combineLatest([
      this.medicalInquiryService.selectedMI$,
      this.medicalInquiryService.showNew$
    ]).pipe(
      distinctUntilChanged(([a, x], [b, y]) => {
        const viewA = x ? 'new' : a ? 'detail' : 'nothing';
        const viewB = y ? 'new' : b ? 'detail' : 'nothing';
        return viewA === viewB;
      }),
      takeUntil(this.destroy$)
    ).subscribe(([selectedMI, showNew]) => {
      if (showNew) {
        this.navService.setChildNavRoot(
          PageName.MedicalInquiryNewComponent,
          PageName.MedicalInquiryNewComponent,
          PageName.MedicalInquiryHomePage
        );
      } else if (selectedMI) {
        this.navService.setChildNavRoot(
          PageName.MedicalInquiryDetailComponent,
          PageName.MedicalInquiryDetailComponent,
          PageName.MedicalInquiryHomePage
        );
      } else {
        this.navService.setChildNavRoot(
          PageName.NothingSelectedView,
          PageName.NothingSelectedView,
          PageName.MedicalInquiryHomePage,
          { deviceString: this.device.deviceFlags?.ios ? 'ios' : 'other' }
        );
      }
      this.cdr.markForCheck();
    });
  }

  ionViewWillLeave(): void {
    this.medicalInquiryService.setFormDirty(false);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.medicalInquiryService.clearSelection();
    // Remove THIS instance's right-pane child nav by reference rather than popping the stack top
    // by name. On tool re-entry (Medical Inquiry menu clicked while already in the tool), the
    // incoming instance's ngOnInit has already pushed its own MedicalInquiryNavigation entry before
    // this outgoing instance is destroyed; popping by name here would clobber the live instance's nav
    // and leave the right pane stuck on "nothing selected" (no detail, no back button on mobile).
    // Removing by ref targets only our own entry and leaves the incoming instance's nav intact.
    // Mirrors the OMNI-59745 EmailTemplatePageComponent fix.
    this.navService.removeChildNavCtrlFromStackByRef(this.navCtrl);
  }

  setTabsData() {
    const hasTeamFA = this.authService.hasFeatureAction(FeatureActionsMap.MEDICAL_INQUIRY_TEAM);
    const hasChildUsers = (this.authService.user?.childUsers?.length ?? 0) > 0;
    const teamEnabled = hasTeamFA && hasChildUsers;
    this.tabsData = [
      {
        displayText: this.translate.instant('MY_MEDICAL_INQUIRIES'),
        value: MI_TAB.MY,
      },
      {
        displayText: teamEnabled ? this.translate.instant('TEAM_MEDICAL_INQUIRIES') : '',
        value: MI_TAB.TEAM,
        disable: !teamEnabled,
      },
    ];
  }

  async selectTab(tabValue: string) {
    if (!(await this.medicalInquiryService.confirmDiscardIfDirty())) return;
    this.selectedTab = tabValue;
    this.medicalInquiryService.selectedTab = tabValue;
    this.medicalInquiryService.selectedTab$.next(tabValue);
    // Reset right pane when switching tabs so list and detail stay in sync
    this.medicalInquiryService.setFormDirty(false);
    this.medicalInquiryService.setSelectedMI(null);
    this.medicalInquiryService.setShowNew(false);
    this.cdr.markForCheck();
  }

  get showDetailOrNew(): boolean {
    return !!this.medicalInquiryService.selectedMI || this.medicalInquiryService.showNew || this.isHostingPushedPage;
  }
}
